import { useCustomerZonesCreate } from "../../hooks/api/v2/customerZones/useCustomerZonesCreate";
import { useBasicDeliveryDefaults } from "../../hooks/useBasicDeliveryDefaults";
import { ParentLink } from "../../components/navigation/ParentLink";
import { ReactNode, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Icon } from "../../components";
import * as ROUTES from "../../constants/routes";
import {
  BadgeGreen,
  BadgeZinc,
  BadgeAmber,
} from "../../components/badge/Badge";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "../../components/dropdown/Dropdown";
import { capitalize, formatCurrency, pluralize } from "@sellar-io/common";
import { InfiniteList } from "../../components/infiniteLoading/InfiniteList";
import { Delivery } from "./Delivery";
import { DeliveryMethodCreateDialog } from "./components/DeliveryMethodCreateDialog";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DataButton,
  H5,
  H6,
  IconType,
  ListItemIconTile,
} from "@sellar-io/components";
import { useFulfillmentTypeList } from "../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { FulfillmentTypeListResponseType } from "@sellar-io/api-v2/types";
import { DeliveryMethodDeleteDialog } from "./components/DeliveryMethodDeleteDialog";
import { useIsSellarAdmin } from "../../hooks/useIsSellarAdmin";
import { useOrderRulesMigrateDeliveryMethods } from "../../hooks/api/v2/orderRules/useOrderRulesMigrateDeliveryMethods";
import { Alert } from "../../components/alert/Alert";
import { useSupplier } from "../../hooks/api/v2/supplier/useSupplier";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { useFeatureFlagStore } from "~/hooks/useFeatureFlags";
import { DeliveryMethodContainerRulesOverview } from "./components/DeliveryMethodContainerRulesOverview";
import { useMediaQuery } from "usehooks-ts";
import { useDeliveryZoneMode } from "../../hooks/useDeliveryZoneMode";
import {
  DeliveryZoneAdvancedSetupDialog,
  DeliveryZonesPrompt,
  RevertToBasicDeliveryZoneDialog,
} from "./components/DeliveryZoneModeDialogs";

export const DeliveryMethodList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const remoteFeatureFlags = useRemoteFeatureFlags();

  const fullyUnbundled = remoteFeatureFlags.isActive("unbundlingReleaseFinal");
  const createZone = useCustomerZonesCreate();
  const isDesktop = useMediaQuery("(min-width: 1000px)");

  const isAdmin = useIsSellarAdmin();
  const unbundlingEnabled = remoteFeatureFlags.isActive(
    "unbundlingReleaseFinal",
  );

  const tradeTermsPlaceholdersEnabled = useFeatureFlagStore().isActive(
    "TRADE_TERM_PLACEHOLDERS",
  );

  const [createNewActive, setCreateNewActive] = useState(false);
  const [advancedSetupActive, setAdvancedSetupActive] = useState(false);
  const [revertActive, setRevertActive] = useState(false);
  const deliveryZoneMode = useDeliveryZoneMode();
  const defaults = useBasicDeliveryDefaults(deliveryZoneMode.isBasicMode);

  const isContainerRulesView =
    new URLSearchParams(location.search).get("view") === "container-rules";

  const [deleteDialogActive, setDeleteDialogActive] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<{
    id: number;
    name: string;
    type: "direct" | "courier" | "haulier";
  }>();

  const fulfillmentTypeListsQuery = useFulfillmentTypeList({
    sortBy: "fulfillmentType",
    sortDir: "asc",
    includeCounts: true,
  });

  const flatLists = useMemo(
    () =>
      fulfillmentTypeListsQuery.data?.pages
        .flat()
        .filter((ft) => ft.fulfillmentType !== "sellarDistribution") ?? [],
    [fulfillmentTypeListsQuery.data],
  );

  const getSubtitle = (
    deliveryMethod: FulfillmentTypeListResponseType[number],
  ) => {
    const noOrderRules = "No order rules";
    const minimumOrderPrice = deliveryMethod.OrderRules?.minimumOrderPrice
      ? `${formatCurrency(deliveryMethod.OrderRules.minimumOrderPrice)} minimum`
      : null;

    const nbOfOrderRules =
      deliveryMethod.OrderRules?.PriceTierOrderRules.length ?? 0;
    const orderRulesCopy =
      nbOfOrderRules > 0
        ? `${nbOfOrderRules} container order ${pluralize("rule", nbOfOrderRules)}`
        : null;

    const orderRules = [minimumOrderPrice, orderRulesCopy]
      .filter(Boolean)
      .join(" and ");

    const flatCharge = deliveryMethod.charge
      ? `${formatCurrency(deliveryMethod.charge)} flat delivery charge`
      : null;

    const nbContainerDeliveryCharges =
      deliveryMethod.FulfillmentTypeContainerDeliveryCharges.length;
    const containerCharges =
      nbContainerDeliveryCharges > 0
        ? `${nbContainerDeliveryCharges} container delivery ${pluralize("charge", nbContainerDeliveryCharges)}`
        : null;

    const nbEligibilityRules =
      deliveryMethod.ContainerTypeEligibilityRules.length;
    const eligibilityRules =
      nbEligibilityRules > 0
        ? `${nbEligibilityRules} courier eligibility ${pluralize("rule", nbEligibilityRules)}`
        : null;

    const nbContainerRules = deliveryMethod.DeliveryMethodContainerRules.length;
    const containerRules =
      nbContainerRules > 0
        ? `${nbContainerRules} container ${pluralize("type", nbContainerRules)} not available`
        : null;

    return [
      ...(!!orderRules ? [orderRules] : [noOrderRules]),
      flatCharge,
      containerCharges,
      unbundlingEnabled ? containerRules : eligibilityRules,
    ]
      .filter(Boolean)
      .join(", ");
  };

  return (
    <Delivery>
      <div className="flex flex-col gap-4">
        {isAdmin && <MigrateDeliveryMethods />}
        {isDesktop && isContainerRulesView && flatLists.length > 0 ? (
          <DeliveryMethodContainerRulesOverview
            deliveryMethods={flatLists}
            isDeliveryMethodsLoading={fulfillmentTypeListsQuery.isLoading}
            fetchNextPage={fulfillmentTypeListsQuery.fetchNextPage}
            isFetching={fulfillmentTypeListsQuery.isFetching}
            onBack={() => navigate(ROUTES.DELIVERY_METHODS)}
          />
        ) : (
          <>
            <div className="flex min-w-0 items-start justify-between gap-2">
              <div className="flex min-w-0 flex-col gap-1">
                <H5>Delivery methods</H5>
                <span className="text-zinc-500">
                  Create direct, courier or pallet delivery methods for your
                  customers to choose from
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {unbundlingEnabled && isDesktop && (
                  <DataButton
                    className="flex items-center gap-1"
                    border={true}
                    onClick={() =>
                      navigate(
                        `${ROUTES.DELIVERY_METHODS}?view=container-rules`,
                      )
                    }
                  >
                    View container rules
                    <Icon icon="CHEVRON_RIGHT" size={0.8} />
                  </DataButton>
                )}
                <DataButton
                  className="flex items-center gap-1"
                  border={true}
                  onClick={() => setCreateNewActive(true)}
                >
                  <Icon icon="PLUS" />
                  New
                </DataButton>
                {deliveryZoneMode.modeAvailable &&
                  deliveryZoneMode.isAdvancedMode && (
                    <Dropdown>
                      <DropdownButton>
                        <DataButton className="py-2.5" border={true}>
                          <Icon
                            icon="DOTS_VERTICAL"
                            className="text-black"
                            size={0.7}
                          />
                        </DataButton>
                      </DropdownButton>
                      <DropdownItems>
                        <DropdownItem
                          className="text-sm text-red-700"
                          onClick={() => setRevertActive(true)}
                        >
                          Revert to basic mode
                        </DropdownItem>
                      </DropdownItems>
                    </Dropdown>
                  )}
              </div>
            </div>

            <InfiniteList
              query={fulfillmentTypeListsQuery}
              className="mb-8 flex flex-col gap-2"
            >
              {flatLists.map((deliveryMethod, idx) => (
                <ListItem
                  key={idx}
                  name={
                    deliveryMethod.name ?? (
                      <>{capitalize(deliveryMethod.fulfillmentType)} delivery</>
                    )
                  }
                  subtitle={getSubtitle(deliveryMethod)}
                  numberOfCustomers={deliveryMethod.numberOfCustomers}
                  numberOfZones={deliveryMethod.numberOfZones}
                  icon={
                    deliveryMethod.fulfillmentType === "direct"
                      ? "TRUCK_OUTLINE"
                      : deliveryMethod.fulfillmentType === "courier"
                        ? "PACKAGE_VARIANT_CLOSED"
                        : "PALLET"
                  }
                  onClick={() =>
                    navigate(
                      ROUTES.DELIVERY_METHOD.href({
                        deliveryMethodId: deliveryMethod.id,
                      }),
                    )
                  }
                  rightComponent={
                    <Dropdown>
                      <DropdownButton>
                        <Button size={"small"} color={"transparent"}>
                          <Icon
                            icon={"PENCIL"}
                            className="text-black"
                            size={0.7}
                          />
                        </Button>
                      </DropdownButton>
                      <DropdownItems>
                        <DropdownItem
                          className="border-b border-zinc-200 text-sm"
                          onClick={() =>
                            navigate(
                              ROUTES.DELIVERY_METHOD.href({
                                deliveryMethodId: deliveryMethod.id,
                              }),
                            )
                          }
                        >
                          Edit
                        </DropdownItem>
                        <DropdownItem
                          className="border-b border-zinc-200 text-sm text-red-700"
                          onClick={() => {
                            setSelectedMethod({
                              id: deliveryMethod.id,
                              name:
                                deliveryMethod.name ??
                                capitalize(deliveryMethod.fulfillmentType),
                              type:
                                deliveryMethod.fulfillmentType !==
                                "sellarDistribution"
                                  ? deliveryMethod.fulfillmentType
                                  : "direct",
                            });
                            setDeleteDialogActive(true);
                          }}
                        >
                          Delete
                        </DropdownItem>
                      </DropdownItems>
                    </Dropdown>
                  }
                />
              ))}
              {flatLists.length === 0 && tradeTermsPlaceholdersEnabled && (
                <PlaceholderListItem onClick={() => setCreateNewActive(true)} />
              )}
            </InfiniteList>

            {deliveryZoneMode.isBasicMode && (
              <>
                <div className="flex flex-col gap-2">
                  <div>
                    <H5>Default delivery methods</H5>
                    <p className="text-zinc-500">
                      Define what delivery information your local and nationwide
                      customers see by default
                    </p>
                  </div>
                  {defaults.query.isError ? (
                    <p role="alert">
                      Unable to load default delivery methods.
                      <Button
                        color="secondary"
                        onClick={() => {
                          void defaults.query.refetch();
                        }}
                      >
                        Retry
                      </Button>
                    </p>
                  ) : defaults.isLoading ? (
                    <p>Loading default delivery methods…</p>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {[
                        {
                          zone: defaults.local,
                          name: "Local",
                          guidance:
                            "Enter postcodes in direct delivery schedules",
                        },
                        {
                          zone: defaults.nationwide,
                          name: "Nationwide",
                          guidance: "All other postcodes",
                        },
                      ].map(({ zone, name, guidance }) => {
                        const methods = zone
                          ? [
                              zone.DrayDeliveryMethod,
                              zone.CourierDeliveryMethod,
                              zone.PalletDeliveryMethod,
                            ]
                              .filter((method) => method != null)
                              .map(
                                (method) =>
                                  method.name ??
                                  capitalize(method.fulfillmentType),
                              )
                              .join(", ")
                          : "";
                        return zone ? (
                          <ParentLink
                            key={name}
                            to={ROUTES.DELIVERY_ZONE.href({
                              deliveryZoneId: zone.id,
                            })}
                            className="flex min-w-0 items-center gap-3 rounded-lg bg-white p-4 hover:bg-zinc-50"
                            aria-label={`Edit ${name} default delivery methods`}
                          >
                            <ListItemIconTile icon="MAP_MARKER" />
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold">{name}</p>
                              <p className="text-sm text-zinc-700">
                                {methods ||
                                  (name === "Nationwide" && fullyUnbundled
                                    ? "Nationwide delivery off"
                                    : guidance)}
                              </p>
                            </div>
                            <Icon icon="PENCIL" size={0.7} />
                          </ParentLink>
                        ) : (
                          <Card key={name} className="bg-white p-4">
                            <p className="font-semibold">{name}</p>
                            {name === "Nationwide" && fullyUnbundled ? (
                              <>
                                <p className="text-sm">
                                  Only buyers in your direct or Sellar
                                  Distribution areas can discover you.
                                </p>
                                <Button
                                  loading={createZone.isPending}
                                  onClick={() =>
                                    createZone.mutate(
                                      {
                                        name: "Nationwide",
                                        type: "thirdParty",
                                        default: true,
                                        PriceTierId: null,
                                        AvailabilityTierId: null,
                                        OrderRulesId: null,
                                      },
                                      {
                                        onSuccess: (zone) =>
                                          navigate(
                                            ROUTES.DELIVERY_ZONE.href({
                                              deliveryZoneId: zone.id,
                                            }),
                                          ),
                                      },
                                    )
                                  }
                                >
                                  Set up nationwide delivery
                                </Button>
                                {createZone.isError && (
                                  <p role="alert">
                                    Unable to restore nationwide delivery.
                                    Please try again.
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-sm">
                                Default zone unavailable. Please contact
                                support.
                              </p>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-sm text-zinc-500">
                    Local postcodes are maintained through direct delivery
                    schedules. Nationwide delivery is optional; contact Sellar
                    to remove its zone and offer only direct or Sellar
                    Distribution delivery.
                  </p>
                </div>
                <DeliveryZonesPrompt
                  onClick={() => setAdvancedSetupActive(true)}
                />
              </>
            )}
          </>
        )}
      </div>
      <DeliveryMethodCreateDialog
        active={createNewActive}
        setActive={setCreateNewActive}
      />
      {selectedMethod && (
        <DeliveryMethodDeleteDialog
          fulfillmentTypeId={selectedMethod.id}
          fulfillmentTypeName={selectedMethod.name}
          fulfillmentTypeType={selectedMethod.type}
          active={deleteDialogActive}
          setActive={setDeleteDialogActive}
        />
      )}
      <DeliveryZoneAdvancedSetupDialog
        active={advancedSetupActive}
        onDismiss={() => setAdvancedSetupActive(false)}
      />
      <RevertToBasicDeliveryZoneDialog
        active={revertActive}
        onDismiss={() => setRevertActive(false)}
      />
    </Delivery>
  );
};

const ListItem = ({
  name,
  subtitle,
  numberOfCustomers,
  numberOfZones,
  onClick,
  rightComponent,
  icon,
}: {
  name: ReactNode;
  subtitle: string;
  numberOfCustomers?: number | null;
  numberOfZones?: number | null;
  onClick: () => void;
  rightComponent?: ReactNode;
  icon: IconType;
}) => {
  return (
    <Card
      onClick={onClick}
      className="flex min-w-0 cursor-pointer items-center gap-4 bg-white p-4 hover:bg-zinc-50"
    >
      <ListItemIconTile icon={icon} />
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <div className="font-semibold">{name}</div>
          <div className="min-w-0 overflow-hidden">
            <p className="overflow-hidden text-sm text-nowrap text-ellipsis text-zinc-700">
              {subtitle}
            </p>
          </div>
        </div>
        <span className="ml-auto flex flex-none items-center gap-2">
          {numberOfZones != null && (
            <>
              {numberOfZones === 0 ? (
                <BadgeZinc icon="MAP_MARKER">0</BadgeZinc>
              ) : (
                <BadgeGreen icon="MAP_MARKER">{numberOfZones}</BadgeGreen>
              )}
            </>
          )}
          {numberOfCustomers != null && (
            <>
              {numberOfCustomers === 0 ? (
                <BadgeZinc icon="ACCOUNT_MULTIPLE">0</BadgeZinc>
              ) : (
                <BadgeGreen icon="ACCOUNT_MULTIPLE">
                  {numberOfCustomers}
                </BadgeGreen>
              )}
            </>
          )}
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={(e) => e.stopPropagation()}
          >
            {rightComponent ?? <Icon icon="CHEVRON_RIGHT" size={1} />}
          </div>
        </span>
      </div>
    </Card>
  );
};

const PlaceholderListItem = ({ onClick }: { onClick: () => void }) => {
  return (
    <Card
      onClick={onClick}
      className="flex cursor-pointer items-center gap-4 bg-white p-4 hover:bg-zinc-50"
    >
      <ListItemIconTile icon="PACKAGE_VARIANT_CLOSED" />
      <div className="flex w-full items-center gap-4 md:flex-nowrap">
        <div className="flex flex-col">
          <p className="flex items-center gap-2 font-semibold">
            First delivery method <BadgeAmber>Setup now</BadgeAmber>
          </p>
          <p className="text-sm">
            Click to setup your first delivery method, you need at least 1
          </p>
        </div>
        <span className="ml-auto flex flex-none items-center gap-2">
          <BadgeZinc icon="ACCOUNT_MULTIPLE">0</BadgeZinc>
          <div>
            <Icon icon="CHEVRON_RIGHT" size={1} />
          </div>
        </span>
      </div>
    </Card>
  );
};

const MigrateDeliveryMethods = () => {
  const [checked, setChecked] = useState(false);
  const migrateMethods = useOrderRulesMigrateDeliveryMethods();

  const { business } = useSellarCurrentBusiness();

  const { data: supplier } = useSupplier(business?.Supplier?.id);

  if (!supplier || supplier.hasMigratedDeliveryMethods) return <></>;

  return (
    <Card className="border border-red-500 bg-white">
      <CardHeader className="flex flex-col bg-white p-4">
        <H5>Migrate this suppliers delivery tiers to delivery methods</H5>
        <H6>This can only be run once</H6>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 p-4">
        <Alert
          type="information"
          title="Additional steps"
          message="Once complete check these additional points"
          className="w-full"
        >
          <div className="flex flex-col gap-4">
            <ul className="w-full list-disc">
              <li>Customer zones - check assigned delviery methods</li>
              <li>
                Customer zones - check the order rules of assigned methods
              </li>
              <li>Customers - check default methods</li>
              <li>
                Customers - check if not default methods need to be overridden
              </li>
            </ul>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-blue-300 bg-blue-200 p-2 hover:bg-blue-300">
              <input
                id="checkbox"
                type="checkbox"
                onChange={(e) => setChecked(e.target.checked)}
              />
              Check to confirm you understand -{" "}
              <b>this message won't show after the migration</b>
            </label>
          </div>
        </Alert>
        <Button
          disabled={!checked}
          loading={migrateMethods.isPending}
          onClick={() => migrateMethods.mutate()}
          className="w-full"
          color="red"
        >
          Migrate
        </Button>
      </CardContent>
    </Card>
  );
};
